import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import '../../css/general.css'
import '../../css/inventory.css';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Select from 'react-select';
import NumberFormat from 'react-number-format';
import ToggleButton from 'react-bootstrap/ToggleButton';
import { customStyles, qualityStyles, formatGroupLabel, getSelectedLang } from '../../common';
import { valueMax } from '../../commonFunctions';
import { getContentWithAction } from '../../apiConnect';
import { OperationType, SeedQuality } from '../../Constants/StorageConstants';
import Spinner from 'react-bootstrap/Spinner';

const PurchseInfoSection = (props) => {
    const { t } = useTranslation();

    /* STATES */
    const [selectedQuality, setSelectedQuality]       = useState(null);
    const [qualities, setQualities]                   = useState([]);
    const [selectedPlant, setSelectedPlant]           = useState(null);
    const [selectedVariety, setSelectedVariety]       = useState(null);
    const [plantSelectList, setPlantSelectList]       = useState([]);
    const [varieties, setVarieties]                   = useState([]);
    const [organicOptions, setOrganicOptions]         = useState([]);
    const [selectedOrganic, setSelectedOrganic]       = useState(null);
    const [amount, setAmount]                         = useState(0);
    const [batchPrice, setBatchPrice]                 = useState(0);
    const [location, setLocation]                     = useState([]);
    const [isFarmOrganic, setIsOrganic]               = useState(true);
    const [isLoading, setIsLoading]                   = useState(false);

    const [fertilizers, setFertilizers]               = useState([]);
    const [selectedFertilizer, setSelectedFertilizer] = useState({});
    const [pesticides, setPesticides]                 = useState([]);
    const [selectedPesticide, setSelectedPesticide]   = useState({});

    /* CONSTANTS */
    const label_noQuality = { label: (t('ChooseQuality')), value: null };
    const label_noOrganic = { label: (t('OrganicSelection')), value: null };
    const qualityOptions = [
        { label: t('SeedQuality_Certified'), value: SeedQuality.Certified },
        { label: t('SeedQuality_FarmSeed'), value: SeedQuality.FarmSeed },
        { label: t('SeedQuality_Other'), value: SeedQuality.Other },
    ];
    const default_text = t('ChoosePlant');
    const default_variety_text = t('ChooseVariety');
    const default_pest_text = t('SelectProduct');
    const default_fert_text = t('ChooseFertilizer');
    const varietyUnit = 'kg';

    useEffect(() => {
        setPlantSelectList(props.sectionData.plants);
        setFertilizers(props.sectionData.fertilizers);
        setPesticides(props.sectionData.pesticides);
        setOrganicOptions([
            { label: '', options: props.sectionData.organicPTypes }
        ]);
    }, [props.sectionData]);

    // * Validate section on change
    useEffect(() => {
        validateSection();
    }, [selectedPlant, selectedFertilizer, selectedVariety, selectedPesticide, selectedQuality, location, selectedOrganic, amount, batchPrice]);

    // * Load varieties and qualities when plant changes
    useEffect(() => {
        setSelectedVariety(null);
        setSelectedQuality(null);

        setIsLoading(true);

        Promise.all([
            getContentWithAction(props.auth.getAccessToken(), 'resource', 'seedQualities', { plantId: selectedPlant?.value, lang: props.lang }),
            getContentWithAction(props.auth.getAccessToken(), 'resource', 'previousVariety', { plantId: selectedPlant?.value, lang: props.lang }),
            getContentWithAction(props.auth.getAccessToken(), 'resource', 'variety', { plantId: selectedPlant?.value, lang: props.lang }),
        ]).then(([qualities, previousVarieties, varieties]) => {
            formatVarietyGroups({ previousVarieties, varieties });
            setQualities(qualities);
            setIsLoading(false);
        }).catch((c) => { setIsLoading(false); });
    }, [selectedPlant]);

    // * Reset selections when storage type changes
    useEffect(() => {
        setSelectedFertilizer(null);
        setSelectedPesticide(null);
        setSelectedVariety(null);
        setSelectedPlant(null);
        setSelectedQuality(null);
        validateSection();
    }, [props.purchaseListObject.purchaseObject.operationType]);

    /**
     * Format variety groups for select input
     * @param {any} varietyData
     */
    const formatVarietyGroups = (data) => {
        let groupsList = [
            { label: t('Previously_Used'), options: data.previousVarieties }
        ];
        for (var v in data.varieties) {
            let tempList = [];
            for (var p in data.varieties[v].groupVarieties) {
                let vary = data.varieties[v].groupVarieties[p];
                let obj2 = { label: vary.label, value: vary.value, unitText: vary.unitText, isMixture: vary.isMixture };
                tempList.push(obj2);
            }
            groupsList = [
                ...groupsList,
                { label: data.varieties[v].groupName, options: tempList }
            ];
        }
        setVarieties([...groupsList]);
    };

    
    // * Validates section
    const validateSection = () => {
        var valid = false;
        // Check selected plant, variety, fertilizer and pesticide based on selected operation type
        switch (props.purchaseListObject.purchaseObject.operationType) {
            case OperationType.Seeds:
                if (selectedPlant?.value && selectedVariety?.value && amount > 0) valid = true;
                break;
            case OperationType.Fertilizers:
                if (selectedFertilizer?.value && amount > 0) valid = true;
                break;
            case OperationType.Pesticides:
                if (selectedPesticide?.value && amount > 0) valid = true;
                break;
            default:
                valid = false;
                break;
        }

        // Update context values
        var updateObject = {
            type: 'UPDATE',
            purchaseObject: {
                plantId: selectedPlant?.value || null,
                varietyId: selectedVariety?.value || null,
                seedQualityId: selectedQuality || SeedQuality.Other,
                fertilizerId: selectedFertilizer?.value || null,
                pesticideId: selectedPesticide?.value || null,
                location: location?.value || null,
                organicStatusId: selectedOrganic?.value || null,
                amount: amount || 0,
                batchPrice: batchPrice || 0
            },
            sectionValidation: {
                productInfoSection: valid
            }
        };
        props.updatePurchaseObject(updateObject);
    }

    return (
        <>
            <Form.Group controlId="productInfo">
                {/* SEEDS */}
                {props.purchaseListObject.purchaseObject.operationType === OperationType.Seeds ? 
                    <>
                        <Form.Label className="text-dark purchase-section-header">{t('Plant')}</Form.Label>
                        <Select
                            className={!selectedPlant?.value ? "text-dark invalid-border" : "text-dark"}
                            value={selectedPlant?.label ? selectedPlant : { label: default_text, value: null }}
                            onChange={(data) => setSelectedPlant(data)}
                            options={plantSelectList}
                            styles={customStyles}
                            formatGroupLabel={formatGroupLabel}
                            maxMenuHeight={250}
                        />

                        <Form.Label className="text-dark purchase-section-header">{t('Sowing_Variety_Code')}</Form.Label>
                        {isLoading ? <Spinner as="span" animation="border" role="status" size="sm" variant="secondary" /> :
                            <Select
                                className={!selectedVariety?.value ? "text-dark invalid-border" : "text-dark"}
                                value={selectedVariety?.label ? selectedVariety : { label: default_variety_text, value: null }}
                                onChange={(data) => setSelectedVariety(data)}
                                options={varieties}
                                formatGroupLabel={formatGroupLabel}
                                styles={customStyles}
                                maxMenuHeight={250}
                            />
                        }

                        <Form.Label className="text-dark purchase-section-header">{t('SeedQuality')}</Form.Label>
                        <ButtonGroup className="seed-quality-group">
                            {qualityOptions.map((radio, i) => (
                                <ToggleButton
                                    key={radio.label + i}
                                    id={radio.label + i}
                                    type="radio"
                                    variant="outline-success"
                                    name={radio.label + i}
                                    value={radio.value}
                                    checked={selectedQuality === radio.value}
                                    onChange={(e) => {
                                        // Cast to number
                                        var value = Number(e.currentTarget.value);
                                        setSelectedQuality(value);
                                    }}
                                >
                                    {radio.label}
                                </ToggleButton>
                            ))}
                        </ButtonGroup>
                        <Select
                            className={`text-dark seed-quality-select ${selectedQuality?.label ? 'selected-highlight' : ''}`}
                            value={selectedQuality?.label ? selectedQuality : label_noQuality}
                            onChange={(data) => setSelectedQuality(data)}
                            options={qualities}
                            styles={selectedQuality?.label ? qualityStyles : customStyles}
                            maxMenuHeight={250}
                        />
                        {isFarmOrganic ? 
                            <>
                                <Form.Label className="text-dark purchase-section-header">{t('OrganicStatus')}</Form.Label>
                                <Select
                                    className={`text-dark`}
                                    value={selectedOrganic?.label ? selectedOrganic : (organicOptions.options?.length > 0 ? organicOptions.options[0] : label_noOrganic)}
                                    onChange={(data) => setSelectedOrganic(data)}
                                    options={organicOptions}
                                    styles={customStyles}
                                    maxMenuHeight={250}
                                    />
                            </>
                            : ''}
                    </>
                    : ''}

                {/* FERTILIZERS */}
                {props.purchaseListObject.purchaseObject.operationType === OperationType.Fertilizers ? 
                    <>
                        <Form.Label className="text-dark purchase-section-header">{t('Fertiliser')}</Form.Label>
                        <Select
                            className={"text-dark"}
                            value={selectedFertilizer?.label ? selectedFertilizer : { label: default_fert_text, value: null }}
                            onChange={(data) => setSelectedFertilizer(data)}
                            options={fertilizers}
                            formatGroupLabel={formatGroupLabel}
                            styles={customStyles}
                            maxMenuHeight={250}
                        />
                    </>
                    : ''}
                
                {/* PESTICIDES */}
                {props.purchaseListObject.purchaseObject.operationType === OperationType.Pesticides ? 
                    <>
                        <Form.Label className="text-dark purchase-section-header">{t('Pesticide')}</Form.Label>
                        <Select
                            className={"text-dark"}
                            value={selectedPesticide?.label ? selectedPesticide : { label: default_pest_text, value: null }}
                            onChange={(data) => setSelectedPesticide(data)}
                            options={pesticides}
                            formatGroupLabel={formatGroupLabel}
                            styles={customStyles}
                            maxMenuHeight={250}
                        />
                    </>
                    : ''}

                <Form.Label className="text-dark purchase-section-header">{t('PurchaseList_Location')}</Form.Label>
                <Form.Control type="text" onChange={(event) => { setLocation(event.target.value) }}
                    value={location}
                    maxLength={100} />
                <div className="char-counter">{location.length}/100</div>

                <Form.Label className="text-dark purchase-section-header">{t('UnitAmount')}</Form.Label>
                <NumberFormat
                    className={amount == null ? "form-control invalid-border" : "form-control"}
                    value={amount}
                    placeholder={' ' + varietyUnit}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        setAmount(values.floatValue);
                    }}
                    displayType={'input'}
                    thousandSeparator={' '}
                    allowedDecimalSeparators={['.', ',']}
                    suffix={'  ' + varietyUnit}
                    allowNegative={false}
                    isAllowed={valueMax}
                />

                <Form.Label className="text-dark purchase-section-header">{t('PurchaseList_BatchPrice')}</Form.Label>
                <NumberFormat
                    className={batchPrice == null ? "form-control invalid-border" : "form-control"}
                    value={batchPrice}
                    placeholder={' ' + '€'}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        setBatchPrice(values.floatValue);
                    }}
                    displayType={'input'}
                    thousandSeparator={' '}
                    allowedDecimalSeparators={['.', ',']}
                    suffix={'  ' + '€'}
                    allowNegative={false}
                    isAllowed={valueMax}
                />
                
            </Form.Group>
        </>
    )
}

export default PurchseInfoSection;